/**
 * The seam between the deck's stored state and the four v1.5 surfaces.
 *
 * Everything the header, the directory, the preview and the thread read comes through this file, and
 * nothing above it knows what a Birtvy blob is. That is the whole point: the storage format is the
 * canvas's, it changes when the canvas changes, and exactly one module (`useDeckBlob`) is allowed to
 * care.
 */
import type { DirectoryItem, DirectoryKind } from '~/design-system/components/v1_5/Directory';
import type { BrandKit } from '~/design-system/components/DeckGen/birtvy/brandKit';
import type { BrandKitBinding } from '~/design-system/components/DeckGen/birtvy/brandKitDefault';
import type { BirtvyDoc } from '~/design-system/components/DeckGen/birtvy/birtvyDoc';
import type { BirtvyImage } from '~/design-system/components/DeckGen/birtvy/birtvyImage';
import type { BirtvySlide } from '~/design-system/components/DeckGen/birtvy/birtvySlide';
import type { BirtvyWeb } from '~/design-system/components/DeckGen/birtvy/web/birtvyWeb';

/**
 * The canvas blob, as stored on the `'Birtvy canvas'` deck row.
 *
 * The five collections below are the ones this workspace reads. The **index signature is
 * load-bearing**: the real blob carries around sixteen keys (`notes`, `wires`, `groups`,
 * `designSystems`, `agentDocs`, `deletedDocIds`, `activeDesignSystemId`, `brandKitBinding`,
 * `docsAdopted`, `recentDocEdits`, `docTheme`, …) and a write that spread only the fields we
 * understand would silently delete the rest of the user's canvas. Read, change one collection, write
 * the whole object back.
 */
export interface DeckBlob {
  title: string;
  slides: BirtvySlide[];
  docs?: BirtvyDoc[];
  images?: BirtvyImage[];
  webs?: BirtvyWeb[];
  [passthrough: string]: unknown;
}

/**
 * One row's underlying object, for whoever needs more than the row.
 *
 * `DirectoryItem` is deliberately thin — a title, a meta line and a thumbnail — because that is all a
 * 46px row draws. The preview needs the artifact's HTML, the document's markdown, the image's source,
 * the site's URL, so it resolves the key back to this.
 */
export type DeckEntity =
  | { kind: 'artifacts'; slide: BirtvySlide }
  | { kind: 'documents'; doc: BirtvyDoc }
  | { kind: 'images'; image: BirtvyImage }
  | { kind: 'websites'; web: BirtvyWeb };

/** What the four surfaces read. Derived from a `DeckBlob`, never assembled by hand above this file. */
export interface DeckContent {
  /** The deck's name. The header shows it and renames it. */
  title: string;
  /** One list per kind, in the deck's own order. In-flight items included, in place. */
  items: Record<DirectoryKind, DirectoryItem[]>;
  /** The object behind a row key, or `undefined` once it has been deleted. */
  entity: (key: string) => DeckEntity | undefined;
  /**
   * The deck-level artifact theme recipe — markdown, written by the style pass, read by anything that
   * generates or re-bakes an artifact so a new one comes out looking like its siblings.
   *
   * `''` when the deck has none, not `undefined`: a caller that has to distinguish "no theme" from
   * "not loaded yet" has `loading` for that, and one nullable fewer at four call sites is worth more
   * than the distinction. It rides the blob's index signature untouched on every write.
   */
  docTheme: string;
  /**
   * The deck's standing brand kit, already resolved (active design system, else the generation
   * snapshot). Null when the deck has none — generation then uses typed theme / the agent instruction.
   */
  brandKit: BrandKit | null;
  /**
   * The kit SNAPSHOT the deck recorded at its last generation, as stored, or null.
   *
   * Beside `brandKit` rather than folded into it because generation needs both halves of the same
   * fact: `brandKit` is what to style with when nothing fresher can be read, and this is the record
   * `resolveKitForGeneration` refreshes on every live read and hands back as the fallback when the read
   * fails. `kitFromBlob` derives `brandKit` from this when no design system is active, so the two never
   * disagree about the kit; only this one carries `at`, `logoOnLight` and the exact tokens the deck holds.
   */
  brandKitBinding: BrandKitBinding | null;
  /**
   * The deck explicitly chose "no kit, the deck's own look". Distinct from `brandKit: null`, which
   * generation treats as "fall back to the account's default kit" — this must silence that tier too.
   */
  kitOptOut: boolean;
  planningDocumentIds?: Record<string, number>;
}

/** A deck with nothing in it. Also what every surface reads while the blob is still in flight. */
export const EMPTY_DECK_CONTENT: DeckContent = {
  title: 'Untitled deck',
  items: { artifacts: [], documents: [], websites: [], images: [] },
  entity: () => undefined,
  docTheme: '',
  brandKit: null,
  brandKitBinding: null,
  kitOptOut: false,
};

/** The four kinds, in the order the kind menu lists them. */
export const DIRECTORY_KINDS: DirectoryKind[] = ['artifacts', 'documents', 'websites', 'images'];
